// src/modules/workspace/DataTable.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Reusable data table — used by Overview + CleaningLab
// View modes: HEAD · TAIL · SAMPLE · SLICE
// Features: sort by column, search filter, null highlighting, type badges
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import { C } from './wsShared';

export default function DataTable({ rows=[], headers=[], types=[], showControls=true }) {
  const [mode,   setMode]   = useState('head');   // head|tail|sample|slice
  const [size,   setSize]   = useState(10);
  const [from,   setFrom]   = useState(0);
  const [to,     setTo]     = useState(50);
  const [search, setSearch] = useState('');
  const [sortCol,setSortCol]= useState(null);
  const [sortDir,setSortDir]= useState('asc');

  const typeMap = useMemo(()=>{ const m={}; types.forEach(t=>{m[t.col]=t.type;}); return m; },[types]);

  // Filter
  const filtered = useMemo(()=>{
    if(!search.trim()) return rows;
    const q=search.toLowerCase();
    return rows.filter(r=>headers.some(h=>String(r[h]??'').toLowerCase().includes(q)));
  },[rows,headers,search]);

  // Sort
  const sorted = useMemo(()=>{
    if(!sortCol) return filtered;
    return [...filtered].sort((a,b)=>{
      const av=a[sortCol]??'', bv=b[sortCol]??'';
      const an=Number(av), bn=Number(bv);
      const cmp=!isNaN(an)&&!isNaN(bn)?an-bn:String(av).localeCompare(String(bv));
      return sortDir==='asc'?cmp:-cmp;
    });
  },[filtered,sortCol,sortDir]);

  // Slice by mode
  const displayed = useMemo(()=>{
    const n=sorted.length;
    if(mode==='head')   return sorted.slice(0,size);
    if(mode==='tail')   return sorted.slice(Math.max(0,n-size));
    if(mode==='sample'){
      if(n<=size) return sorted;
      const step=Math.floor(n/size);
      return Array.from({length:size},(_,i)=>sorted[i*step]);
    }
    if(mode==='slice')  return sorted.slice(Math.max(0,from),Math.min(n,to));
    return sorted.slice(0,size);
  },[sorted,mode,size,from,to]);

  const toggleSort = col => {
    if(sortCol===col) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const typeColor = t => t==='numeric'?C.cyan:t==='categorical'?C.gold:C.t3;

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
      {showControls&&(
        <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
          {/* Mode pills */}
          <div style={{ display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:3,padding:2 }}>
            {['head','tail','sample','slice'].map(m=>(
              <button key={m} onClick={()=>setMode(m)} style={{
                padding:'4px 11px',border:'none',borderRadius:2,cursor:'pointer',
                background:mode===m?C.cyan:'transparent',
                color:mode===m?C.void:C.t3,
                fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,textTransform:'uppercase',
              }}>{m}</button>
            ))}
          </div>

          {/* Row count (head/tail/sample) */}
          {mode!=='slice'&&(
            <div style={{ display:'flex',alignItems:'center',gap:5 }}>
              <span style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>ROWS</span>
              {[5,10,25,50,100].map(n=>(
                <button key={n} onClick={()=>setSize(n)} style={{
                  padding:'3px 7px',border:`1px solid ${size===n?C.cyan:C.b1}`,
                  background:size===n?C.cyan+'20':'transparent',
                  color:size===n?C.cyan:C.t4,
                  fontFamily:'JetBrains Mono,monospace',fontSize:9,borderRadius:2,cursor:'pointer',
                }}>{n}</button>
              ))}
            </div>
          )}

          {/* Slice range */}
          {mode==='slice'&&(
            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
              <span style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>FROM</span>
              <input type="number" value={from} min={0} max={rows.length-1} onChange={e=>setFrom(+e.target.value)}
                style={numStyle} />
              <span style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>TO</span>
              <input type="number" value={to} min={1} max={rows.length} onChange={e=>setTo(+e.target.value)}
                style={numStyle} />
            </div>
          )}

          {/* Search */}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{
            marginLeft:'auto',background:C.deep,border:`1px solid ${C.b1}`,color:C.t1,
            padding:'5px 10px',fontSize:10,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none',width:150,
          }} />
        </div>
      )}

      <div style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>
        Showing {displayed.length} of {sorted.length}{search&&` (filtered from ${rows.length})`} rows
        {sortCol&&` · sorted by ${sortCol} ${sortDir==='asc'?'↑':'↓'}`}
      </div>

      <div style={{ overflowX:'auto',borderRadius:4,border:`1px solid ${C.b1}` }}>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:11,minWidth:Math.max(headers.length*90,300) }}>
          <thead>
            <tr style={{ background:C.deep }}>
              <th style={th}><span style={{ color:C.t4 }}>#</span></th>
              {headers.map(h=>{
                const t=typeMap[h]; const sorted_=sortCol===h;
                return (
                  <th key={h} onClick={()=>toggleSort(h)} style={{ ...th,cursor:'pointer',userSelect:'none' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:5 }}>
                      <span style={{ fontSize:8,color:typeColor(t),fontWeight:700 }}>{t==='numeric'?'#':'Aa'}</span>
                      <span style={{ color:sorted_?C.cyan:C.t2 }}>{h}</span>
                      {sorted_&&<span style={{ color:C.cyan,fontSize:8 }}>{sortDir==='asc'?'▲':'▼'}</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr style={{ background:C.raised }}>
              <td style={{ ...td,fontSize:8,color:C.t4 }}>type</td>
              {headers.map(h=>(
                <td key={h} style={{ ...td,fontSize:8,color:typeColor(typeMap[h]) }}>{typeMap[h]||'?'}</td>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.length===0?(
              <tr><td colSpan={headers.length+1} style={{ padding:24,textAlign:'center',color:C.t4 }}>No rows</td></tr>
            ):displayed.map((row,ri)=>{
              const globalIdx = mode==='tail'?rows.length-displayed.length+ri:mode==='slice'?from+ri:ri;
              return (
                <tr key={ri} style={{ borderBottom:`1px solid ${C.b0}`,background:ri%2?C.raised+'60':'transparent' }}>
                  <td style={{ ...td,color:C.t4,fontSize:9 }}>{globalIdx}</td>
                  {headers.map(h=>{
                    const val=row[h]; const empty=val===''||val==null;
                    return (
                      <td key={h} style={{ ...td,color:empty?C.red:typeMap[h]==='numeric'?C.t1:C.t2,background:empty?C.red+'0a':'transparent',fontFamily:typeMap[h]==='numeric'?'JetBrains Mono,monospace':'inherit' }}>
                        {empty?<em style={{ fontSize:9,color:C.red }}>null</em>:String(val).slice(0,60)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding:'7px 12px',textAlign:'left',fontSize:10,color:'#8396b3',fontWeight:700,letterSpacing:'.07em',borderBottom:'1px solid #0f1a2e',whiteSpace:'nowrap' };
const td = { padding:'6px 12px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' };
const numStyle = { width:64,background:'#060b14',border:'1px solid #1e293b',color:'#e2e8f0',padding:'4px 7px',fontSize:10,fontFamily:'JetBrains Mono,monospace',borderRadius:2,outline:'none' };
